/**
 * Customer orders + quotes API client module.
 *
 * Wraps the base axios instance from SH-AUTH-002 (`api/client.ts`) with
 * typed functions for every customer-facing order/quote endpoint. All
 * functions:
 *  1. Call `apiClient` with the documented URL and params.
 *  2. Unwrap the platform envelope via `unwrap`/`throwAsApiError` so callers
 *     always receive an `ApiError` on the failure path.
 *  3. Return typed DTO shapes extracted from the envelope's `data` slot.
 *
 * DTOs are co-located here so the types live close to the API layer that
 * produces them and can be imported by consumers without coupling to the
 * slice module (`@dfq/shared/stores/orders`).
 *
 * SF-ORD-001.
 */

import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap, unwrapList } from '@dfq/shared/api/_envelope';
import type { PaginatedResponse } from '@dfq/shared/api/types';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AcceptQuoteResponseWire,
  QuoteLineItemWire,
  QuoteRequestWire,
  QuoteWire,
} from '@dfq/shared/types/quote';

// ─────────────────────────────────────────────────────────────────────────────
// Status enums
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Canonical order lifecycle status strings emitted by the platform.
 * Exported as a const object so callers can reference `OrderStatusEnum.SHIPPED`
 * rather than the raw string `"shipped"`. Mirrors
 * `App\Services\Orders\Enums\OrderStatus`.
 *
 * The `StatusTimeline` capsule (SH-ORD-001) imports `OrderStatusEnum` and
 * `OrderStatus` from this module.
 */
export const OrderStatusEnum = {
  PENDING_PAYMENT: 'pending_payment',
  PAYMENT_CONFIRMED: 'payment_confirmed',
  DESIGN_IN_PROGRESS: 'design_in_progress',
  IN_PREPRESS: 'in_prepress',
  ARTWORK_ISSUE: 'artwork_issue',
  APPROVED_FOR_PRINT: 'approved_for_print',
  PRINTING: 'printing',
  LAMINATING_FINISHING: 'laminating_finishing',
  QUALITY_CHECK: 'quality_check',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
} as const;

/** Derived union of all order status strings. */
export type OrderStatus = (typeof OrderStatusEnum)[keyof typeof OrderStatusEnum];

/**
 * Canonical quote-request status strings emitted by the platform. Mirrors
 * `App\Services\Quotes\Enums\QuoteStatus`.
 */
export const QuoteStatusEnum = {
  PENDING: 'pending',
  QUOTED: 'quoted',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  EXPIRED: 'expired',
} as const;

/** Derived union of all quote status strings. */
export type QuoteStatus = (typeof QuoteStatusEnum)[keyof typeof QuoteStatusEnum];

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot sub-interfaces
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Product information frozen at order placement time. Stored as JSON so
 * the order detail page renders the product name/sku accurately even if
 * the live product has since been edited.
 */
export interface ProductSnapshot {
  name: string;
  sku_prefix: string;
}

/**
 * Material information frozen at order placement time.
 */
export interface MaterialSnapshot {
  name: string;
  manufacturer?: string;
}

/**
 * Selected option values frozen at order placement time. Records the
 * option-group key → selected label/value pairs for the order detail
 * renderer.
 */
export interface OptionsSnapshot {
  lamination?: string;
  [key: string]: string | undefined;
}

/**
 * Vehicle information frozen at order placement time. Present only when
 * the order item's `sizing_mode` is `vehicle_lookup`.
 */
export interface VehicleSnapshot {
  make: string;
  model: string;
  year: number;
  trim?: string;
  total_sqft?: number;
}

/**
 * Dimensions frozen at order placement time. The fields present depend on
 * `sizing_mode` — vehicle items populate `total_sqft`; custom-dimension
 * items populate `width_inches` + `height_inches`.
 */
export interface DimensionsSnapshot {
  total_sqft?: number;
  width_inches?: number | null;
  height_inches?: number | null;
}

/**
 * Single add-on applied to an order item at placement time.
 */
export interface AddOnSnapshot {
  name: string;
  price: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core DTO interfaces
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Artwork file attached to an order item. Returned inside the nested
 * `artwork_files` array on each `OrderItemDTO` when fetching full order detail.
 * The `download_url` is a presigned S3 URL valid for a short window.
 *
 * SF-ORD-003.
 */
export interface ArtworkFileDTO {
  id: number;
  original_filename: string;
  file_size_bytes: number | null;
  validation_status: 'pending' | 'passed' | 'failed' | 'manual_review';
  /** Presigned S3 download URL. May be null if the file has not been processed. */
  download_url: string | null;
}

/**
 * Preview-capable artwork file reference returned inside
 * `lineItems[].attachedFiles[]` from `GET /api/v1/account/orders/:id`.
 *
 * `previewUrl` and `thumbnailUrl` are presigned S3 URLs — both are `null`
 * when `previewStatus !== 'ready'`. The container uses these to drive
 * `ArtworkProofThumbnail` props directly.
 *
 * SF-ACCT-ARTWORK.
 */
export interface AttachedFileDTO {
  id: number;
  /** Foreign key to the `artwork_files` table row. */
  artworkFileId: number | null;
  /** True when this is the primary design file for the line item. */
  isPrimary: boolean;
  originalFilename: string;
  mimeType: string | null;
  /** Preview generation state. `'ready'` means presigned URLs are available. */
  previewStatus: 'pending' | 'processing' | 'ready' | 'unavailable' | 'failed';
  /** Presigned full-resolution preview URL. Non-null only when `previewStatus === 'ready'`. */
  previewUrl: string | null;
  /** Presigned thumbnail URL. Non-null only when `previewStatus === 'ready'`. */
  thumbnailUrl: string | null;
}

/**
 * Single order item as returned by `GET /api/v1/account/orders/:id`. All
 * snapshot fields are typed sub-interfaces rather than `Record<string, unknown>`
 * so consuming components get type-safe access to snapshot values without
 * runtime casting.
 */
/**
 * One line on an order, as `/account/orders/{id}` emits it under `lineItems`.
 *
 * Much thinner than the cart-side model: the platform currently returns no
 * material, options, vehicle or add-on snapshot, and `productName` arrives
 * empty because `product_snapshot_json` only stores `{"product_id": n}`.
 * Consumers must degrade gracefully rather than assume a rich snapshot.
 */
export interface OrderItemDTO {
  id: number;
  orderId?: number;
  /** Currently emitted as null even when the row has a product_id. */
  productId?: number | null;
  /** Empty string until the platform snapshots a product name at checkout. */
  productName?: string;
  /** Piece count for per-piece products; square footage for area-priced ones. */
  quantity?: number | null;
  /** Pricing unit frozen at checkout; absent on lines whose product is gone. */
  pricingUnit?: 'per_sqft' | 'per_piece' | null;
  lineTotalCents?: number;
  sizing?: DimensionsSnapshot | null;
  contourJobId?: number | null;
  customShapePathId?: number | null;
  contourOutputSnapshotJson?: unknown;
  customShapePathJson?: unknown;
  shapeSnapshotJson?: unknown;
  /** Preview-capable attached artwork references. */
  attachedFiles?: AttachedFileDTO[];
}

/**
 * Single lifecycle status-history entry. One record is emitted per
 * `Order → status` transition. The `StatusTimeline` capsule renders these
 * as an ordered list of steps.
 */
export interface OrderStatusHistoryDTO {
  id: number;
  from_status: OrderStatus | null;
  to_status: OrderStatus;
  changed_by_user_id: number | null;
  notes: string | null;
  created_at: string;
}

/**
 * Carrier shipment record. Attached to a full order detail response and
 * also returned by the tracking endpoint (with additional real-time carrier
 * fields).
 */
export interface ShipmentDTO {
  id: number;
  carrier: 'fedex' | 'ups' | 'usps' | string;
  service_level: string;
  tracking_number: string;
  shipped_at: string | null;
  estimated_delivery_at: string | null;
  delivered_at: string | null;
  shipping_cost?: string;
  /** Real-time carrier status text — only populated on the tracking endpoint. */
  status?: string;
  /** Real-time carrier events — only populated on the tracking endpoint. */
  events?: Array<{
    timestamp: string;
    description: string;
    location: string;
  }>;
}

/**
 * Applied discount entry as returned inside a full order detail. The
 * `type` discriminator drives how the UI labels the discount line
 * (coupon code vs. promotion name).
 */
export interface OrderDiscountDTO {
  id: number;
  type: 'coupon' | 'promotion';
  code: string | null;
  description: string;
  /** Decimal string. Always positive — the UI prefixes a `−`. */
  amount: string;
}

/**
 * Address snapshot stored on the order at placement time. Frozen copy of
 * the address so the detail page renders the destination as it was when
 * the customer checked out, not their current default.
 */
export interface AddressSnapshotDTO {
  first_name: string;
  last_name: string;
  company: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/**
 * Customer-safe summary of a production job attached to an order item.
 *
 * This is the subset of `ProductionJobDTO` that the customer-facing Order
 * detail endpoint surfaces. Operator names, internal time logs, and raw
 * material usage are NOT included — those are gated by `#[RequiresPermission]`
 * on the platform DTO's `READ_PERMISSION_MAP`.
 *
 * Keys are camelCase because that is what `CustomerProductionJobTemplate`
 * emits. They were snake_case here, so every read came back undefined, no
 * timeline events were ever built, and production progress was invisible to
 * every customer — the type being hand-written is why nothing caught it.
 *
 * SF-FULFILLMENT-001.
 */
export interface ProductionJobSummaryDTO {
  /** DB primary key of the `production_jobs` row. */
  id: number;
  /** Scoped job number (e.g. `"DFQ-J-2026-00042"`). */
  jobNumber: string;
  /**
   * Current production stage status.
   * Maps to `ProductionJobStatus` enum values:
   * `queued | printing | laminating | finishing | qa_check | complete | on_hold | failed`
   */
  status: string;
  /**
   * ISO 8601 timestamp when the job entered its current stage.
   * Used to render the "started" entry in the production timeline.
   */
  startedAt: string | null;
  /**
   * ISO 8601 timestamp when the job completed all stages.
   * Present only for `complete` or `failed` terminal states.
   */
  completedAt: string | null;
}

/**
 * Full order detail as returned by `GET /api/v1/account/orders/:id`.
 * Includes all nested relations: items, status history, discounts, shipments.
 */
/**
 * Normalise an address snapshot from the wire.
 *
 * PHP encodes an empty associative array as `[]`, so the platform emits an
 * array rather than an object when no snapshot was captured. Treat that as
 * absent instead of rendering `undefined` into the page.
 */
export function asAddressSnapshot(value: unknown): AddressSnapshotDTO | null {
  if (value === null || value === undefined || Array.isArray(value)) return null;
  return value as AddressSnapshotDTO;
}

/**
 * Payment states an order can hold. Mirrors `App\Enums\OrderPaymentStatus`.
 *
 * Note there is no `paid` — an order is `authorized` at checkout and only
 * becomes `captured` when production starts.
 */
export const OrderPaymentStatusEnum = {
  PENDING: 'pending',
  AUTHORIZED: 'authorized',
  DEPOSIT_PAID: 'deposit_paid',
  BALANCE_DUE: 'balance_due',
  CAPTURED: 'captured',
  VOIDED: 'voided',
  REQUIRES_ACTION: 'requires_action',
  DISPUTED: 'disputed',
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'partially_refunded',
} as const;

export type OrderPaymentStatus =
  (typeof OrderPaymentStatusEnum)[keyof typeof OrderPaymentStatusEnum];

/**
 * A customer order.
 *
 * Fields mirror what `/account/orders` and `/account/orders/{id}` actually
 * emit: camelCase keys and integer cents. Money is never a decimal string
 * here — use `formatCurrencyFromCents` to render it.
 *
 * The list and detail responses carry different subsets, so anything only one
 * of them returns is optional. Notably the **detail response omits `status`**,
 * which is why `status` is optional rather than required.
 */
export interface OrderDTO {
  id: number;
  orderNumber: string;
  /** Lifecycle status. Present on list responses; absent on detail today. */
  status?: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  /** Order total in integer cents. */
  totalCents: number;
  /** Number of line items — list responses only. */
  itemCount?: number;
  /** Null until the order is actually placed. */
  placedAt?: string | null;

  // ── Detail-only ────────────────────────────────────────────────────────
  userId?: number;
  subtotalCents?: number;
  taxCents?: number;
  shippingCostCents?: number;
  /** Total including any post-placement adjustments. */
  currentOrderTotalCents?: number;
  paidAt?: string | null;
  authorizedAt?: string | null;
  capturedAt?: string | null;
  activePaymentId?: number | null;

  // ── Deposit / balance (PL-DEPOSIT-011) ────────────────────────────────
  /** Taken at checkout, non-refundable. Null on pre-deposit orders. */
  depositAmountCents?: number | null;
  depositPaidAt?: string | null;
  /** Charged at print approval. Null until the balance is actually taken. */
  balanceAmountCents?: number | null;
  balancePaidAt?: string | null;
  /** Everything captured against the order so far. */
  capturedAmountCents?: number | null;
  /** Server-computed — never derive this client-side. */
  outstandingCents?: number | null;

  lineItems?: OrderItemDTO[];
  /** Address snapshot taken at checkout. */
  shippingAddressSnapshotJson?: AddressSnapshotDTO | unknown[];
  billingAddressSnapshotJson?: AddressSnapshotDTO | unknown[];
  /**
   * Customer-safe production job summaries. Operator names, time logs and
   * material usage are filtered out server-side.
   */
  productionJobs?: ProductionJobSummaryDTO[];
  estimatedProductionStartAt?: string | null;
  estimatedShipAt?: string | null;

  // ── Not yet emitted by the platform ────────────────────────────────────
  // Kept so consumers compile and degrade to an empty state rather than
  // crashing. Remove once the API grows them.
  statusHistory?: OrderStatusHistoryDTO[];
  discounts?: OrderDiscountDTO[];
  shipments?: ShipmentDTO[];
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
}

/** Wire shape of `POST /account/orders/{id}/pay-balance`. */
export interface PayOrderBalanceResultDTO {
  orderId: number;
  /** False when the order already owed nothing — not a failure. */
  collected: boolean;
  /** What was actually charged, which may exceed the outstanding shown. */
  amountCents: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Quote DTOs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `QuoteDTO`/`QuoteRequestDTO` are the platform's actual (camelCase)
 * `toArray()` shapes, defined once in `@dfq/shared/types/quote` and shared
 * with the public guest quote-request flow (`@dfq/shared/api/publicQuotes`)
 * — both read the exact same platform DTOs. Re-exported under these names
 * so existing importers (`@dfq/shared/stores/orders`, account quote views)
 * keep working.
 *
 * SF-QUOTE-103: this module previously declared a hand-written snake_case
 * shape (`product_name`, `submitted_at`, `quote.total` as a decimal string,
 * etc.) that did not match the wire at all — masked by hand-shaped MSW
 * fixtures in tests. Do not reintroduce a second, invented shape here.
 */
export type QuoteLineItemDTO = QuoteLineItemWire;
export type QuoteDTO = QuoteWire;
export type QuoteRequestDTO = QuoteRequestWire;

// ─────────────────────────────────────────────────────────────────────────────
// Tracking response
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Response shape from `GET /api/v1/account/orders/:id/tracking`. Includes real-time
 * carrier data beyond what is stored on the shipment record.
 */
/** Wire shape of `GET /account/orders/{id}/tracking` — carries no order id. */
export interface OrderTrackingDTO {
  shipments: ShipmentDTO[];
  aggregateStatus?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter + pagination params
// ─────────────────────────────────────────────────────────────────────────────

export interface GetOrdersParams {
  status?: OrderStatus;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface GetQuotesParams {
  status?: QuoteStatus;
  page?: number;
  per_page?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strip `undefined` / empty-string entries so the request URL stays clean
 * (no `?status=&search=` sentinels).
 */
function compactParams(
  params: Record<string, string | number | undefined>,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      out[key] = value;
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// API functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET `/api/v1/account/orders` — paginated list of the authenticated
 * customer's orders. Accepts optional filter + pagination params.
 */
export async function getOrders(
  params: GetOrdersParams = {},
): Promise<PaginatedResponse<OrderDTO>> {
  try {
    const response = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(
      '/account/orders',
      {
        // Left-hand names are what the endpoint reads; the params bag keeps the
        // caller-facing names. They differed, so the order history's date range
        // and page size were dropped in transit and never filtered anything.
        params: compactParams({
          status: params.status,
          placed_from: params.date_from,
          placed_to: params.date_to,
          search: params.search,
          page: params.page,
          limit: params.per_page,
        }),
      },
    );
    return unwrapList<OrderDTO>(response.data, response.status, 'orders');
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET `/api/v1/account/orders/:id` — full detail for a single order,
 * including nested items, status history, discounts, and shipments.
 */
export async function getOrderById(id: number): Promise<OrderDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ order: OrderDTO }>>(
      `/account/orders/${id}`,
    );
    // The envelope nests the order under `data.order`.
    return unwrap(response.data, response.status).order;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET `/api/v1/account/orders/:id/tracking` — real-time carrier tracking data for
 * the specified order's shipment(s). May include live carrier status updates
 * beyond what is stored on the Shipment record.
 */
export async function getOrderTracking(orderId: number): Promise<OrderTrackingDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<OrderTrackingDTO>>(
      `/account/orders/${orderId}/tracking`,
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET `/api/v1/account/quotes` — paginated list of the authenticated
 * customer's quote requests. Each entry includes the top-level
 * `QuoteRequestDTO` fields and a summary of the associated `QuoteDTO` if
 * one has been prepared.
 */
export async function getQuotes(
  params: GetQuotesParams = {},
): Promise<PaginatedResponse<QuoteRequestDTO>> {
  try {
    const response = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(
      '/account/quotes',
      {
        params: compactParams({
          status: params.status,
          page: params.page,
          per_page: params.per_page,
        }),
      },
    );
    // The list envelope nests the collection under `data.quoteRequests`
    // (`ApiResponse::withListData('quoteRequests', ...)`), NOT `data.quotes`.
    return unwrapList<QuoteRequestDTO>(response.data, response.status, 'quoteRequests');
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET `/api/v1/account/quotes/:id` — full detail for a single quote request,
 * including the full `QuoteDTO` with line items if one has been prepared.
 */
export async function getQuoteById(id: number): Promise<QuoteRequestDTO> {
  try {
    // The envelope nests the payload under `data.quoteRequest`
    // (`QuoteRequestTemplate::apply()` returns `['quoteRequest' => ...]`).
    const response = await apiClient.get<ApiEnvelope<{ quoteRequest: QuoteRequestDTO }>>(
      `/account/quotes/${id}`,
    );
    return unwrap(response.data, response.status).quoteRequest;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Damage claims
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Response payload from `POST /api/v1/account/orders/:id/damage-claims`.
 * Returns the newly-created damage claim record.
 *
 * SF-FULFILLMENT-003 / PL-FULFILLMENT-012.
 */
export interface DamageClaimDTO {
  id: number;
  order_id: number;
  shipment_id: number;
  status: 'pending' | 'under_review' | 'approved' | 'denied';
  description: string;
  /** Number of photos attached to the claim. */
  photo_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * POST `/api/v1/account/orders/:orderId/damage-claims` — file a shipping
 * damage claim.
 *
 * Accepts `multipart/form-data` with `shipment_id`, `description`, and up
 * to 5 photo files. Photos are stored in S3 by the platform action. Returns
 * the newly-created `DamageClaimDTO`.
 *
 * Backend: PL-FULFILLMENT-012 (`FileShippingDamageClaimController`).
 * SF-FULFILLMENT-003.
 */
export async function fileDamageClaim(
  orderId: number,
  payload: {
    shipment_id: number;
    description: string;
    /** Object keys from a direct-to-S3 upload. Preferred. */
    photo_s3_keys?: string[];
    /** Raw files, multipart. Only viable where no WAF body limit applies. */
    photos?: File[];
  },
): Promise<DamageClaimDTO> {
  // Keys go as JSON: a few hundred bytes rather than the photos themselves.
  // Multipart bodies over 8KB are rejected at the edge before they reach the
  // API, and every real photo is larger than that.
  const useKeys = (payload.photo_s3_keys?.length ?? 0) > 0;

  let body: FormData | Record<string, unknown>;
  let headers: Record<string, string> | undefined;

  if (useKeys) {
    body = {
      shipment_id: payload.shipment_id,
      description: payload.description,
      photo_s3_keys: payload.photo_s3_keys,
    };
  } else {
    const formData = new FormData();
    formData.append('shipment_id', String(payload.shipment_id));
    formData.append('description', payload.description);
    for (const photo of payload.photos ?? []) {
      formData.append('photos[]', photo);
    }
    body = formData;
    headers = { 'Content-Type': 'multipart/form-data' };
  }

  try {
    const response = await apiClient.post<ApiEnvelope<DamageClaimDTO>>(
      `/account/orders/${orderId}/damage-claims`,
      body,
      headers ? { headers } : undefined,
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Cancellation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Response payload from `POST /api/v1/account/orders/:id/cancel`.
 * The platform returns the order id and new status for optimistic UI updates.
 *
 * SF-FULFILLMENT-002 / PL-FULFILLMENT-010.
 */
export interface CancelOrderResponseDTO {
  orderId: number;
  status: 'cancelled';
}

/**
 * POST `/api/v1/account/orders/:id/cancel` — customer-initiated cancellation.
 *
 * Allowed when the order status is still in the pre-production window
 * (`pending_payment`, `payment_confirmed`, `in_prepress`, `artwork_issue`,
 * `approved_for_print`). Returns 409 when the order is in `printing` or
 * beyond — the caller receives an `ApiError` with the platform envelope
 * message, which the container surfaces inline.
 *
 * Backend: PL-FULFILLMENT-010 (`CancelOrderController`).
 */
export async function cancelOrder(
  orderId: number,
  reason?: string,
): Promise<CancelOrderResponseDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<CancelOrderResponseDTO>>(
      `/account/orders/${orderId}/cancel`,
      reason ? { reason } : {},
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Quote mutations
// ─────────────────────────────────────────────────────────────────────────────

/** Payload for `POST /api/v1/account/quotes`. */
export interface CreateQuoteRequestPayload {
  product_id?: number | null;
  vehicle_id?: number | null;
  description: string;
  custom_dimensions?: Record<string, unknown> | null;
  material_preference?: string | null;
  artwork_file_ids?: number[] | null;
}

/**
 * `data` shape returned by `POST /api/v1/account/quotes/:id/accept` —
 * `{ quote: QuoteDTO.toArray(), order: { id, clientSecret, requiresAction,
 * chargedCents } }`, both top-level under `data` (not nested under a
 * `quoteRequest`/`quote` wrapper key). `order.id` is non-null on success —
 * `AcceptQuoteAction` creates the locked-price order synchronously
 * (PL-INFRA-009) before this response is built.
 */
export type AcceptQuoteResponseData = AcceptQuoteResponseWire;

/**
 * POST `/api/v1/account/quotes` — submit a new customer quote request.
 * Returns the created `QuoteRequestDTO` (under `data.quoteRequest`) with
 * status `pending`.
 */
export async function createQuoteRequest(
  payload: CreateQuoteRequestPayload,
): Promise<QuoteRequestDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<{ quoteRequest: QuoteRequestDTO }>>(
      '/account/quotes',
      payload,
    );
    const data = unwrap(response.data, response.status);
    return data.quoteRequest;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/quotes/:id/accept` — accept a sent quote.
 *
 * PL-WRAP-112 — `payment_method_id` is now required. The platform charges
 * the quote's `amountDueBasis` amount (deposit or full) with the card,
 * retained `off_session` for any later balance. Returns the accepted quote
 * and the newly-created locked-price order, including `clientSecret`/
 * `requiresAction` for a 3-D Secure challenge the caller must finish with
 * Stripe.js. A 409 means the quote is already settled or a concurrent
 * accept is mid-claim; a 400 means the card was declined or a 3DS
 * challenge was abandoned — the caller may retry with a new
 * `paymentMethodId` while the order stays `pending_payment`.
 */
export async function acceptQuote(
  id: number,
  paymentMethodId: string,
): Promise<AcceptQuoteResponseData> {
  try {
    const response = await apiClient.post<ApiEnvelope<AcceptQuoteResponseData>>(
      `/account/quotes/${id}/accept`,
      { payment_method_id: paymentMethodId },
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/quotes/:id/decline` — decline a sent quote.
 * Returns the updated `QuoteRequestDTO` (under `data.quoteRequest`).
 */
export async function declineQuote(id: number, reason?: string): Promise<QuoteRequestDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<{ quoteRequest: QuoteRequestDTO }>>(
      `/account/quotes/${id}/decline`,
      reason ? { reason } : {},
    );
    const data = unwrap(response.data, response.status);
    return data.quoteRequest;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/orders/{id}/pay-balance` — charge what the order still
 * owes to the customer's card.
 *
 * The customer's own route into the same collection an admin triggers at print
 * approval. Reached from the balance-declined email and from order detail.
 *
 * Omit `paymentMethodId` to re-charge the card already on the order — correct
 * for an `authentication_required` decline, where the card is fine and only the
 * bank's challenge was missing. Pass a `pm_…` when a new card is needed.
 *
 * Resolves to `null` when the order owes nothing, which the platform treats as
 * success rather than an error so a double-submit is harmless. A declined card
 * throws with HTTP 402 and the reason under `context.declineCode`.
 */
export async function payOrderBalance(
  orderId: number,
  paymentMethodId?: string,
): Promise<PayOrderBalanceResultDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<{ balance: PayOrderBalanceResultDTO }>>(
      `/account/orders/${orderId}/pay-balance`,
      paymentMethodId ? { paymentMethodId } : {},
    );
    return unwrap(response.data, response.status).balance;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Convenience grouping export so consumers can `import { ordersApi }` if
 * they prefer the namespace style.
 */
export const ordersApi = {
  getOrders,
  getOrderById,
  getOrderTracking,
  getQuotes,
  getQuoteById,
  createQuoteRequest,
  acceptQuote,
  declineQuote,
  cancelOrder,
  fileDamageClaim,
  payOrderBalance,
};
